import React, { useEffect, useRef, useState } from "react";
import styles from "./Reports.module.css";

const H = 220;
const PAD = { top: 22, bottom: 24, left: 8, right: 8 };

// Bars side by side per group, an optional note above each group (coloured by noteTone), and a legend on top.
// A series with `hatched` draws as light diagonal stripes, for figures that are estimates rather than booked.
export default function BarChart({ groups, series, format, noteTone = "", ariaLabel }) {
  // Drawn at the figure's real width so bars spread across wide and narrow panels alike, without stretching text.
  const ref = useRef(null);
  const [W, setW] = useState(640);
  useEffect(() => {
    if (!ref.current || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => setW(Math.max(280, Math.round(entry.contentRect.width))));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  const max = Math.max(...groups.flatMap((g) => series.map((s) => g[s.key])), 1);
  const slot = (W - PAD.left - PAD.right) / Math.max(groups.length, 1);
  const barW = Math.min(30, (slot * 0.7) / series.length);
  const y = (v) => ((H - PAD.top - PAD.bottom) * Math.max(v, 0)) / max;

  return (
    <figure ref={ref} className={styles.chart}>
      <figcaption className={styles.legend}>
        {series.map((s) => (
          <span key={s.key}><i className={s.hatched ? styles.hatchedKey : ""} style={{ background: s.hatched ? undefined : s.color }} />{s.label}</span>
        ))}
      </figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} height={H} role="img" aria-label={ariaLabel}>
        <defs>
          <pattern id="reportHatch" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="8" height="8" fill="#dbe8f7" />
            <line x1="0" y1="0" x2="0" y2="8" stroke="#bcd4ee" strokeWidth="4" />
          </pattern>
        </defs>
        <line x1={PAD.left} x2={W - PAD.right} y1={H - PAD.bottom} y2={H - PAD.bottom} className={styles.axis} />
        {groups.map((g, i) => {
          const left = PAD.left + slot * i + (slot - barW * series.length) / 2;
          const top = Math.max(...series.map((s) => y(g[s.key])));
          return (
            <g key={g.label}>
              {series.map((s, si) => {
                const h = y(g[s.key]);
                return (
                  <rect key={s.key} x={left + si * barW} y={H - PAD.bottom - h} width={barW - 3} height={h} rx={3} fill={s.hatched ? "url(#reportHatch)" : s.color}>
                    <title>{`${g.label} · ${s.label}: ${format(g[s.key])}`}</title>
                  </rect>
                );
              })}
              {g.note && (
                <text x={left + (barW * series.length) / 2} y={H - PAD.bottom - top - 6} className={`${styles.chartNote} ${noteTone}`}>
                  {g.note}
                </text>
              )}
              <text x={PAD.left + slot * i + slot / 2} y={H - 6} className={styles.chartLabel}>{g.label}</text>
            </g>
          );
        })}
      </svg>
    </figure>
  );
}
